/* eslint-disable react/no-unknown-property */
import React, { MutableRefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Action, Card, FormHandlers,
  HStack,
  ICON_HEART_OUTLINE,
} from '@doveit/bricks';
import useFilters, { Filters, FiltersMapper } from '../../../hooks/use-filters/useFilters';
import { Intent } from '../../../providers/api/dtos';
import CreateIntentWithAppointment from '../../containers/create-intent-with-appointment/CreateIntentWithAppointment';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';

interface CreateIntentPageFilters extends Filters {
  contactId?: string;
  propertyId?: number;
}

export const CREATE_INTENT_SUCCESS_MESSAGE = 'Interesse d\'acquisto salvato.';
export const CREATE_INTENT_ERROR_MESSAGE = 'Non é stato possibile salvare l\'interesse d\'acquisto.';
export const LOAD_CONTACT_ERROR_MESSAGE = 'Errore durante il caricamento del contatto. Riprova inserendo i dati manualmente.';
export const DUPLICATED_INTENT_WARNING_MESSAGE = 'Non é stato possibile creare un nuovo interesse perché già esistente.';

export const filtersMapper: FiltersMapper<CreateIntentPageFilters> = {
  contactId: (value) => value,
  propertyId: (value) => (!Number.isNaN(Number(value)) ? Number(value) : undefined),
};

const CreateIntentPage: React.FC = () => {
  const navigate = useNavigate();
  const { filters: { contactId, propertyId } } = useFilters(filtersMapper);
  const [intentIsCreated, setIntentIsCreated] = React.useState(false);
  const [formActionDisabled, setFormActionDisabled] = React.useState(false);

  const [isSaving, setIsSaving] = React.useState(false);

  const formRef = React.useRef<FormHandlers>(null) as MutableRefObject<FormHandlers>;

  const onIntentCreated = React.useCallback(
    () => setIntentIsCreated(true),
    [],
  );

  const onSuccess = React.useCallback(
    (intent: Intent) => navigate(`/intents/${intent.id}`),
    [navigate],
  );

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  return (
    <SimplePageLayout wrapperSize="M">
      <Card paddingSize="M">
        <Card.Header
          icon={{ path: ICON_HEART_OUTLINE }}
          title={!intentIsCreated ? 'Aggiungi interesse' : 'Aggiungi appuntamento'}
        />
        <Card.Content>
          <CreateIntentWithAppointment
            propertyId={propertyId}
            contactId={contactId}
            onIntentCreated={onIntentCreated}
            onAppointmentCreated={onSuccess}
            onAppointmentAborted={onSuccess}
            createIntentFormRef={formRef}
            onFormStatusChanged={setFormActionDisabled}
            onStartSaving={() => setIsSaving(true)}
            onFinishSaving={() => setIsSaving(false)}
          />
        </Card.Content>
        {!intentIsCreated && (
          <>
            <Card.Divider />
            <Card.Content>
              <HStack justifyContent="flex-end">
                <Action
                  label="Aggiungi interesse"
                  color="primary"
                  emphasis="high"
                  aria-label="Pulsante per aggiungere un nuovo interesse d'acquisto"
                  onClick={submitForm}
                  loading={isSaving}
                  disabled={formActionDisabled}
                />
              </HStack>
            </Card.Content>
          </>
        )}
      </Card>
    </SimplePageLayout>
  );
};

export default CreateIntentPage;
